<template>
  <div
    class="flex flex-col w-full min-h-0 flex-1 overflow-hidden
      [font-family:AsenPro,sans-serif] text-[clamp(1.125rem,2.5vw+0.5rem,1.5rem)]"
  >
    <div
      class="box-border mx-auto my-0 flex w-full max-w-full min-h-0 flex-1 flex-col
        overflow-y-auto overflow-x-hidden pt-0 px-2 max-[480px]:px-1 lg:px-0"
    >
      <!-- Loading -->
      <div
        v-if="loading"
        class="m-0 flex min-h-0 flex-1 flex-col justify-center overflow-x-hidden"
      >
        <div
          class="mx-auto w-full max-w-lg shrink-0 rounded-lg bg-white p-6 text-center shadow-md sm:p-8"
        >
          <i
            class="pi pi-spin pi-spinner my-6 inline-block text-4xl text-[var(--q-text-secondary,#666)]"
            aria-hidden="true"
          />
          <span class="sr-only">{{ $t('AssessmentIntroComponent.loading') || 'Loading...' }}</span>
        </div>
      </div>

      <!-- Not configured -->
      <div
        v-else-if="!hasRequiredIntroText"
        class="m-0 flex min-h-0 flex-1 flex-col justify-center overflow-x-hidden"
      >
        <div
          class="mx-auto w-full max-w-lg shrink-0 rounded-lg bg-white p-6 text-center shadow-md sm:p-8"
        >
          <p class="text-base text-[var(--q-text-secondary,#666)] sm:text-lg">
            {{ $t('AssessmentIntroComponent.introNotConfigured') }}
          </p>
        </div>
      </div>

      <!-- Main: height follows content so the white card grows on long copy (scroll on parent) -->
      <template v-else>
        <div
          class="flex w-full shrink-0 flex-col rounded-lg bg-white p-6 shadow-md md:p-8"
        >
          <div
            class="w-full text-[clamp(0.9375rem,1.2vw+0.5rem,1.0625rem)] leading-relaxed"
          >
            <section class="mb-8 pt-1 pb-2 sm:mb-10" aria-labelledby="txt_headline">
              <h1
                id="txt_headline"
                class="mb-4 text-[clamp(1.25rem,3vw,1.75rem)] font-bold leading-tight
                  text-[var(--q-text-primary,#1E2019)] sm:mb-5"
              >
                {{ introTitle }}
              </h1>
              <div
                id="txt_introContent"
                class="intro-prose mb-6 sm:mb-8"
                v-html="introContent"
              />
              <div class="mt-6 sm:mt-8">
                <Button
                  type="button"
                  unstyled
                  name="start-assessment-intro"
                  id="btn_oim"
                  class="btn-primary-accent min-h-11 px-6 py-2.5 text-base"
                  @click.prevent="navigateToQuestionnaire"
                >
                  {{ introButtonText }}
                </Button>
              </div>
            </section>

            <section
              v-if="hasInstructions"
              id="div_howItWorks"
              class="py-6 sm:py-7 border-t border-[var(--q-border-color,#e7e7e7)]"
              aria-labelledby="instructions-heading"
            >
              <h2
                id="instructions-heading"
                class="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-[var(--q-text-primary,#1E2019)]"
              >
                {{ instructionsHeading }}
              </h2>
              <div
                class="intro-prose mb-0"
                v-html="instructionsContent"
              />
            </section>

            <section
              v-if="hasAdditionalInfo"
              id="div_definition"
              class="py-6 sm:py-7 border-t border-[var(--q-border-color,#e7e7e7)]"
              aria-labelledby="additional-heading"
            >
              <h2
                id="additional-heading"
                class="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-[var(--q-text-primary,#1E2019)]"
              >
                {{ additionalInfoHeading }}
              </h2>
              <div
                class="intro-prose mb-0"
                v-html="additionalInfoContent"
              />
            </section>
          </div>
        </div>
      </template>
    </div>

    <provide-feedback-component />
  </div>
</template>

<script>
import { Button } from 'primevue';
import ProvideFeedbackComponent from '@/components/global/ProvideFeedbackComponent.vue';
import NetworkAssessment from '@/helpers/networking/NetworkAssessment';
// eslint-disable-next-line import/no-cycle
import NetworkAuth from '@/helpers/networking/NetworkAuth';

export default {
  name: 'AssessmentIntroComponent',
  props: ['id'],
  components: { Button, ProvideFeedbackComponent },
  data() {
    return {
      assessment: null,
      loading: true,
    };
  },
  computed: {
    lang() {
      const locale = this.$i18n?.locale || 'en';
      return locale;
    },
    intro() {
      return this.assessment?.config?.intro || null;
    },
    hasRequiredIntroText() {
      if (!this.intro?.enabled) return false;
      const title = this.intro.title && (this.intro.title[this.lang]?.trim());
      const content = this.intro.content && (this.intro.content[this.lang]?.trim());
      const buttonText = this.intro.buttonText && (this.intro.buttonText[this.lang]?.trim());
      return !!(title && content && buttonText);
    },
    introTitle() {
      if (!this.intro?.title) return this.$t('AssessmentIntroComponent.defaultTitle');
      return this.intro.title[this.lang];
    },
    introContent() {
      if (!this.intro?.content) return '';
      const html = this.intro.content[this.lang];
      return html || '<p></p>';
    },
    introButtonText() {
      if (!this.intro?.buttonText) return this.$t('AssessmentIntroComponent.defaultButtonText');
      return this.intro.buttonText[this.lang];
    },
    hasInstructions() {
      const content = this.intro?.instructionsContent?.[this.lang];
      return !!content && content.trim() !== '';
    },
    instructionsHeading() {
      if (!this.intro?.instructionsHeading) return this.$t('AssessmentIntroComponent.defaultInstructionsHeading');
      return this.intro.instructionsHeading[this.lang];
    },
    instructionsContent() {
      const content = this.intro?.instructionsContent;
      if (!content) return '';
      return content[this.lang];
    },
    hasAdditionalInfo() {
      const content = this.intro?.additionalInfoContent?.[this.lang];
      return !!content && content.trim() !== '';
    },
    additionalInfoHeading() {
      if (!this.intro?.additionalInfoHeading) return '';
      return this.intro.additionalInfoHeading[this.lang];
    },
    additionalInfoContent() {
      const content = this.intro?.additionalInfoContent;
      if (!content) return '';
      return content[this.lang];
    },
    introBreadCrumbs() {
      const label = this.assessment?.representationProperties?.assessmentName?.[this.lang]
        || this.assessment?.representationProperties?.assessmentName?.en
        || this.assessment?.assessmentRepresentationProperties?.assessmentName?.[this.lang]
        || this.assessment?.assessmentRepresentationProperties?.assessmentName?.en
        || this.$t('BreadCrumbs.Module2OIM');
      return [
        {
          route: this.$route.fullPath,
          isActive: true,
          label,
        },
      ];
    },
  },
  async mounted() {
    await this.loadAssessment();
  },
  beforeRouteLeave(to, from, next) {
    if (from.query.externalToken) {
      localStorage.removeItem('jwt');
    }
    next();
  },
  methods: {
    questionnaireQuery(withFromIntro) {
      const q = { id: this.id };
      if (withFromIntro) q.fromIntro = '1';
      if (this.$route.query.externalToken) {
        q.externalToken = this.$route.query.externalToken;
      }
      return q;
    },
    goToQuestionnaire(withFromIntro) {
      this.$router.push({
        path: '/evaluation/questionnaire',
        query: this.questionnaireQuery(withFromIntro),
      });
    },
    async loadAssessment() {
      this.loading = true;
      try {
        const { externalToken } = this.$route.query;
        const existingJwt = localStorage.getItem('jwt') || localStorage.getItem('token');

        if (externalToken && this.id) {
          localStorage.removeItem('jwt');
          try {
            const { data } = await NetworkAssessment.authenticateExternal(this.id, externalToken);
            if (data?.token) {
              localStorage.setItem('jwt', data.token);
            }
          } catch (authError) {
            console.error('AssessmentIntroComponent: external auth failed', authError);
            this.$toast.add({
              severity: 'error',
              summary: this.$t('AssessmentIntroComponent.authFailed')
                || 'Authentication failed. The link may be invalid or expired.',
              detail: '',
              life: 5000,
            });
            return;
          }
        } else if (!existingJwt) {
          this.$router.push({ path: '/auth', query: { redirect: this.$route.fullPath } });
          return;
        } else if (!externalToken && NetworkAuth.isTokenExpired(existingJwt)) {
          try {
            await this.$store.dispatch('refreshToken');
          } catch {
            this.$router.push({ path: '/auth', query: { redirect: this.$route.fullPath } });
            return;
          }
        }

        await this.$store.dispatch('getAssessment', {
          id: this.id,
          locale: this.$i18n.locale,
        });
        this.assessment = this.$store.state.questionnaireManagement?.assessment || null;
        if (!this.assessment) {
          this.$router.push('/');
          return;
        }
        if (!this.assessment.config?.intro?.enabled || !this.hasRequiredIntroText) {
          this.goToQuestionnaire(false);
          return;
        }
      } catch (e) {
        console.error('AssessmentIntroComponent: failed to load assessment', e);
        this.$router.push('/');
      } finally {
        this.loading = false;
      }
    },
    navigateToQuestionnaire() {
      this.goToQuestionnaire(true);
    },
  },
};
</script>

<style scoped>
/* v-html prose only — Tailwind cannot target injected markup without :deep */
.intro-prose :deep(p) {
  margin-bottom: 1rem;
  color: var(--color-text-secondary, #374151);
}

.intro-prose :deep(p:last-child) {
  margin-bottom: 0;
}

.intro-prose :deep(ul) {
  list-style-type: disc;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

.intro-prose :deep(ol) {
  list-style-type: decimal;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

.intro-prose :deep(li) {
  margin-bottom: 0.35rem;
}

.intro-prose :deep(li::marker) {
  color: var(--color-orange, #ea580c);
}

.intro-prose :deep(h1),
.intro-prose :deep(h2),
.intro-prose :deep(h3) {
  font-weight: 600;
  line-height: 1.3;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--color-text-primary, #111827);
}

.intro-prose :deep(h1) { font-size: 1.375rem; }
.intro-prose :deep(h2) { font-size: 1.25rem; }
.intro-prose :deep(h3) { font-size: 1.125rem; }

.intro-prose :deep(a) {
  color: var(--color-orange);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.intro-prose :deep(a:hover) {
  color: var(--color-fulvous);
}

.intro-prose :deep(strong) {
  font-weight: 600;
}

.intro-prose :deep(blockquote) {
  margin: 1rem 0;
  padding-left: 1rem;
  border-left: 4px solid var(--color-orange);
  color: var(--color-text-secondary, #374151);
  font-style: italic;
}
</style>
